/** The editor's undo coalescing memory: when the last entry was opened and by
 *  which category. */
export type UndoCoalesceState = { at: number; category: string | null };

/** No previous entry: the next push always opens one. Also what undo/redo
 *  reset to, so an edit after an undo is always undoable on its own. */
export const INITIAL_UNDO_COALESCE_STATE: UndoCoalesceState = { at: 0, category: null };

/**
 * Decide whether an undo push opens a new entry or folds into the previous
 * one. Pushes without a category always open an entry. A push folds when it
 * repeats the previous push's category within `windowMs`.
 *
 * - Fixed window (default, the editor's historical rule): measured from the
 *   push that opened the entry, so a long run opens a new entry every window.
 * - Sliding window: every folded push restarts the window, so a held arrow key
 *   or slider step run is ONE entry until the user pauses for a full window.
 */
export function resolveUndoPush(
  last: UndoCoalesceState,
  { category, now, windowMs, sliding = false }: {
    category?: string;
    now: number;
    windowMs: number;
    sliding?: boolean;
  },
): { push: boolean; next: UndoCoalesceState } {
  if (category && last.category === category && now - last.at < windowMs) {
    return { push: false, next: sliding ? { at: now, category } : last };
  }
  return { push: true, next: { at: now, category: category ?? null } };
}
